<template>
  <FriedModal
    :closable="showCloseButton"
    :teleport-to="teleportTo"
    @enter="handleConfirm"
    @close="handleClose"
  >
    <template #header>
      <FriedModalHeader :header="title" />
    </template>
    <FriedParagraph>{{ text }}</FriedParagraph>
    <slot />
    <template #footer>
      <FriedModalButtonsFooter>
        <FriedButton
          v-if="showSecondaryButton"
          id="cancel-button"
          ref="secondaryButtonRef"
          class="prompt-button"
          :button-type="ButtonType.Secondary"
          :disabled="loading"
          data-external="prompt-reject-button"
          @click="handleCancel"
        >
          {{ rejectText }}
        </FriedButton>
        <FriedButton
          id="delete-button"
          ref="mainButtonRef"
          class="prompt-button"
          :button-type="buttonType"
          :color="color"
          :loaded="done"
          :loading="loading"
          data-external="prompt-confirm-button"
          @click="handleConfirm"
        >
          {{ confirmText }}
        </FriedButton>
      </FriedModalButtonsFooter>
    </template>
  </FriedModal>
</template>

<script lang="ts">
import type { RainbowColor } from '@getaccept/fried-tofu';
import { ButtonType } from '@getaccept/fried-tofu';
import { useFocus } from '@vueuse/core';
import type { PropType } from 'vue';
import { defineComponent, computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';

type InitialElementFocus = 'main-button' | 'secondary-button' | 'none';

export default defineComponent({
  props: {
    title: {
      type: String,
      default: '',
    },
    text: {
      type: String,
      default: '',
    },
    mainButtonText: {
      type: String,
      default: '',
    },
    secondaryButtonText: {
      type: String,
      default: '',
    },
    buttonType: {
      type: String as PropType<ButtonType>,
      default: ButtonType.Primary,
    },
    showSecondaryButton: {
      type: Boolean,
      default: true,
    },
    showCloseButton: {
      type: Boolean,
      default: true,
    },
    loading: {
      type: Boolean,
    },
    done: {
      type: Boolean,
    },
    color: {
      type: String as PropType<RainbowColor>,
      default: '',
    },
    teleportTo: {
      type: String,
      default: undefined,
    },
    autoFocus: {
      type: String as PropType<InitialElementFocus>,
      default: 'none',
    },
  },
  emits: ['confirm', 'cancel', 'close'],
  setup(props, { emit }) {
    const { t } = useI18n();

    const mainButtonRef = ref(null);
    const secondaryButtonRef = ref(null);
    const { focused: mainButtonFocus } = useFocus(mainButtonRef);
    const { focused: secondaryButtonFocus } = useFocus(secondaryButtonRef);

    const confirmText = computed(() => props.mainButtonText || t('delete'));
    const rejectText = computed(() => props.secondaryButtonText || t('cancel'));

    const handleConfirm = () => {
      emit('confirm');
    };

    const handleCancel = () => {
      emit('cancel');
    };

    const handleClose = () => {
      emit('close');
    };

    onMounted(() => {
      switch (props.autoFocus) {
        case 'main-button':
          mainButtonFocus.value = true;
          break;
        case 'secondary-button':
          secondaryButtonFocus.value = true;
          break;
      }
    });

    return {
      ButtonType,
      confirmText,
      rejectText,
      handleConfirm,
      handleCancel,
      handleClose,
      mainButtonRef,
      secondaryButtonRef,
    };
  },
});
</script>
<style lang="scss" scoped>
:deep(.prompt-button) {
  min-width: 88px;
  justify-content: center;
}
</style>
